import axios from 'axios';
import { API_BASE_URL } from '../config';

// Configurar axios para admin (usa cookies)
const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin`,
  withCredentials: true, // IMPORTANTE: siempre enviar cookies
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para manejar errores de sesión expirada
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Sesión expirada o no válida
      console.warn('⚠️ Sesión de admin expirada');
      sessionStorage.removeItem('adminData');

      // Redirigir al login si no estamos ya ahí
      if (window.location.pathname !== '/admin-login') {
        window.location.href = '/admin-login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTENTICACIÓN
// ============================================

export const adminLogout = async () => {
  const response = await adminApi.post('/logout');
  sessionStorage.removeItem('adminData');
  return response.data;
};

// ============================================
// GESTIÓN DE CLIENTES
// ============================================

export const getClientes = async () => {
  const response = await adminApi.get('/clientes');
  return response.data;
};

export const createCliente = async (clienteData) => {
  const response = await adminApi.post('/clientes', clienteData);
  return response.data;
};

export const updateCliente = async (id, clienteData) => {
  const response = await adminApi.put(`/clientes/${id}`, clienteData);
  return response.data;
};

export const deleteCliente = async (id) => {
  const response = await adminApi.delete(`/clientes/${id}`);
  return response.data;
};

// ============================================
// GESTIÓN DE EMPRESAS
// ============================================

export const getCompaniesByCliente = async (idCliente) => {
  const response = await adminApi.get(`/clientes/${idCliente}/companies`);
  return response.data;
};

// Inactivar empresa (soft delete)
export const deactivateCompany = async (companyId) => {
  const response = await adminApi.patch(`/companies/${companyId}/deactivate`);
  return response.data;
};

// Reactivar empresa
export const reactivateCompany = async (companyId) => {
  const response = await adminApi.patch(`/companies/${companyId}/reactivate`);
  return response.data;
};

// Eliminar empresa permanentemente (hard delete)
export const deleteCompany = async (companyId) => {
  const response = await adminApi.delete(`/companies/${companyId}`);
  return response.data;
};

// Inactivar múltiples empresas (soft delete masivo)
export const bulkDeactivateCompanies = async (companyIds) => {
  const response = await adminApi.post('/companies/bulk-deactivate', { companyIds });
  return response.data;
};

// Eliminar múltiples empresas permanentemente (hard delete masivo)
export const bulkDeleteCompanies = async (companyIds) => {
  const response = await adminApi.post('/companies/bulk-delete', { companyIds });
  return response.data;
};

// ============================================
// GESTIÓN DE PAGOS
// ============================================

export const createPago = async (pagoData) => {
  const response = await adminApi.post('/pagos', pagoData);
  return response.data;
};

export const updatePago = async (id, pagoData) => {
  const response = await adminApi.put(`/pagos/${id}`, pagoData);
  return response.data;
};

export const deletePago = async (id) => {
  const response = await adminApi.delete(`/pagos/${id}`);
  return response.data;
};

// ============================================
// ESTADÍSTICAS
// ============================================

export const getStats = async () => {
  const response = await adminApi.get('/stats');
  return response.data;
};

// ============================================
// NOTIFICACIONES
// ============================================

export const getNotifLogs = async (params = {}) => {
  const response = await adminApi.get('/notificaciones/logs', { params });
  return response.data;
};

export const getNotifConfigs = async () => {
  const response = await adminApi.get('/notificaciones/config');
  return response.data;
};

export const createNotifConfig = async (data) => {
  const response = await adminApi.post('/notificaciones/config', data);
  return response.data;
};

export const updateNotifConfig = async (id, data) => {
  const response = await adminApi.put(`/notificaciones/config/${id}`, data);
  return response.data;
};

export const toggleNotifConfig = async (id, activo) => {
  const response = await adminApi.patch(`/notificaciones/config/${id}/toggle`, { activo });
  return response.data;
};

export const deleteNotifConfig = async (id) => {
  const response = await adminApi.delete(`/notificaciones/config/${id}`);
  return response.data;
};

export const enviarNotifAhora = async (id) => {
  const response = await adminApi.post(`/notificaciones/config/${id}/enviar-ahora`);
  return response.data;
};

// ============================================
// FACTURADOR ELECTRÓNICO
// ============================================

export const getFacturadorEmpresas = async (mes) => {
  const response = await adminApi.get('/facturador/empresas', { params: { mes } });
  return response.data;
};

export const activarFacturador = async (idCompany) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/activar`);
  return response.data;
};

export const configurarFacturador = async (idCompany, datos) => {
  const response = await adminApi.put(`/facturador/empresas/${idCompany}`, datos);
  return response.data;
};

export const configurarLimitesFacturador = async (idCompany, datos) => {
  const response = await adminApi.put(`/facturador/empresas/${idCompany}/limites`, datos);
  return response.data;
};

export const getFacturadorDocumentos = async (idCompany, mes) => {
  const response = await adminApi.get(`/facturador/empresas/${idCompany}/documentos`, { params: { mes } });
  return response.data;
};

export const getFacturadorAuditoria = async (idDocumento) => {
  const response = await adminApi.get(`/facturador/documentos/${idDocumento}`);
  return response.data;
};

export const getFacturadorAdicionales = async (mes) => {
  const response = await adminApi.get('/facturador/adicionales', { params: { mes } });
  return response.data;
};

export const getReceptoresFacturador = async (idCompany) => {
  const response = await adminApi.get(`/facturador/empresas/${idCompany}/receptores`);
  return response.data;
};

export const vistaPreviaSetFacturador = async (idCompany, texto) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/set/vista-previa`, { texto });
  return response.data;
};

// datos: { texto, clase: 'basico'|'factura_compra', receptores, giroCorregido? }
export const enviarSetFacturador = async (idCompany, datos) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/set/enviar`, datos, { timeout: 120000 });
  return response.data;
};

export const getEnviosCertificacion = async (idCompany) => {
  const response = await adminApi.get(`/facturador/empresas/${idCompany}/certificacion/envios`);
  return response.data;
};

export const consultarEstadoSetFacturador = async (idCompany, lote) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/envios/${lote}/estado`);
  return response.data;
};

export const reenviarSetFacturador = async (idCompany, lote) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/envios/${lote}/reenviar`);
  return response.data;
};

export const descargarMuestrasSet = async (idCompany, lote) => {
  const response = await adminApi.get(`/facturador/empresas/${idCompany}/certificacion/envios/${lote}/muestras`, { responseType: 'blob' });
  return response.data;
};

// datos: { texto, clase: 'libro_ventas'|'libro_compras', proveedor?: { rut, razonSocial } }
export const enviarLibroFacturador = async (idCompany, datos) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/libro/enviar`, datos, { timeout: 120000 });
  return response.data;
};

export const getLibrosCertificacion = async (idCompany) => {
  const response = await adminApi.get(`/facturador/empresas/${idCompany}/certificacion/libros`);
  return response.data;
};

export const consultarEstadoLibroFacturador = async (idCompany, libro) => {
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/libros/${libro}/estado`);
  return response.data;
};

// archivo: el XML de DTE emitidos que entrega "Respaldo de DTE" del SII
export const vistaPreviaSimulacionFacturador = async (idCompany, archivo) => {
  const formData = new FormData();
  formData.append('archivo', archivo);
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/simulacion/vista-previa`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const enviarSimulacionFacturador = async (idCompany, archivo) => {
  const formData = new FormData();
  formData.append('archivo', archivo);
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/certificacion/simulacion/enviar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
  return response.data;
};

// rechazos: { clave del documento: motivo }; los demás documentos se aceptan
export const responderIntercambioFacturador = async (idCompany, archivo, rechazos = {}) => {
  const formData = new FormData();
  formData.append('archivo', archivo);
  if (Object.keys(rechazos).length > 0) formData.append('rechazos', JSON.stringify(rechazos));
  const response = await adminApi.post(`/facturador/empresas/${idCompany}/intercambio`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// ─── Inversiones exterior: tipos de cambio al 31-12 (solo EFE, D9) ───────────

export const getTiposCambioInversiones = async (anioComercial) => {
  const response = await adminApi.get('/inversiones-exterior/tipos-cambio', { params: { anio_comercial: anioComercial } });
  return response.data;
};

// datos: { anio_comercial, moneda, valor_clp, fecha_vigencia, fuente }
export const registrarTipoCambioInversiones = async (datos) => {
  const response = await adminApi.post('/inversiones-exterior/tipos-cambio', datos);
  return response.data;
};

// datos: { valor_clp, fecha_vigencia, fuente }; responde 409 con las cargas validadas que usan la tasa
export const corregirTipoCambioInversiones = async (id, datos) => {
  const response = await adminApi.put(`/inversiones-exterior/tipos-cambio/${id}`, datos);
  return response.data;
};

// ─── Plan de cuentas: propuestas de cuentas nuevas ───────────────────────────

// estado: PENDIENTE (por defecto), APROBADA, RECHAZADA o TODAS
export const getPropuestasCuenta = async (estado) => {
  const response = await adminApi.get('/plan-cuentas/propuestas', { params: { estado } });
  return response.data;
};

// Crea la cuenta con el siguiente código libre de su partida
export const aprobarPropuestaCuenta = async (id) => {
  const response = await adminApi.post(`/plan-cuentas/propuestas/${id}/aprobar`);
  return response.data;
};

export const rechazarPropuestaCuenta = async (id, motivoRechazo) => {
  const response = await adminApi.post(`/plan-cuentas/propuestas/${id}/rechazar`, { motivo_rechazo: motivoRechazo });
  return response.data;
};

export default adminApi;
